/**
 * Ingredients API - Doctor Recipe Ingredients CRUD Operations
 * Handles getting, creating, updating, and deleting recipe ingredients
 */

import http from '../../../client';
import { errorHandler } from '../../../errorHandler';
import type { ApiResponse } from '../../../types';
import type { Ingredient, CreateIngredientData } from './types';
import { extractResponseData } from './helpers';
import {
  transformIngredientToBackend,
  transformIngredientFromBackend,
} from './transformers';

/**
 * Gets all ingredients for a remedy
 * Endpoint: GET /recipes/{recipeId}/ingredients
 * @param recipeId - ID of the remedy
 * @returns Array of ingredients
 */
export const getRecipeIngredients = async (
  recipeId: string | number,
): Promise<ApiResponse<Ingredient[]>> => {
  try {
    const recipeIdStr = String(recipeId);
    const response = await http.get(`/recipes/${recipeIdStr}/ingredients`);

    // Extract data from response (handles nested { success, data: [...] } structure)
    const extractedData = extractResponseData(response);

    // Map backend response to frontend format
    const ingredients = Array.isArray(extractedData)
      ? extractedData.map((ing) => transformIngredientFromBackend(ing))
      : [];

    return {
      success: true,
      data: ingredients,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Adds a new ingredient to a remedy
 * Endpoint: POST /recipes/{recipeId}/ingredients
 * @param recipeId - ID of the remedy
 * @param ingredient - Ingredient data to add
 * @returns Created ingredient object
 */
export const saveRecipeIngredients = async (
  recipeId: string | number,
  ingredient: CreateIngredientData,
): Promise<ApiResponse<Ingredient>> => {
  try {
    const recipeIdStr = String(recipeId);

    // Transform frontend format to backend format
    const payload = transformIngredientToBackend(ingredient);

    const response = await http.post(
      `/recipes/${recipeIdStr}/ingredients`,
      payload,
    );

    // Extract and transform response
    const ingredientData = extractResponseData(response);
    const mappedIngredient = transformIngredientFromBackend(ingredientData);

    return {
      success: true,
      data: mappedIngredient,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Updates an existing ingredient in a remedy
 * Endpoint: POST /recipes/{recipeId}/ingredients
 * @param recipeId - ID of the remedy
 * @param ingredientId - ID of the ingredient to update
 * @param ingredient - Updated ingredient data
 * @returns Updated ingredient object
 */
export const updateRecipeIngredient = async (
  recipeId: string | number,
  ingredientId: string | number,
  ingredient: CreateIngredientData,
): Promise<ApiResponse<Ingredient>> => {
  try {
    const recipeIdStr = String(recipeId);

    // Transform frontend format to backend format
    const payload = {
      ...transformIngredientToBackend(ingredient),
      id: ingredientId, // Include relation ID for update (renamed from ingredient_id)
      // Note: `ingredient_id` (master ingredient ID) is included from transformIngredientToBackend if present
    };

    const response = await http.post(
      `/recipes/${recipeIdStr}/ingredients`,
      payload,
    );

    // Extract and transform response
    const ingredientData = extractResponseData(response);
    const mappedIngredient = transformIngredientFromBackend(ingredientData);

    return {
      success: true,
      data: mappedIngredient,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Reorders recipe ingredients by ingredient IDs
 * Endpoint: PATCH /recipes/{recipeId}/ingredients/reorder
 * @param recipeId - ID of the remedy
 * @param ingredientIds - Array of recipe_ingredient IDs in desired display order
 */
export const reorderRecipeIngredients = async (
  recipeId: string | number,
  ingredientIds: (string | number)[],
): Promise<ApiResponse<void>> => {
  try {
    const recipeIdStr = String(recipeId);
    await http.patch(`/recipes/${recipeIdStr}/ingredients/reorder`, {
      ingredientIds: ingredientIds.map((id) =>
        typeof id === 'string' ? parseInt(id, 10) : id,
      ),
    });

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};

/**
 * Deletes an ingredient from a remedy
 * Endpoint: DELETE /recipes/{recipeId}/ingredients/{ingredientId}
 * @param recipeId - ID of the remedy
 * @param ingredientId - ID of the ingredient to delete
 */
export const deleteRecipeIngredient = async (
  recipeId: string | number,
  ingredientId: string | number,
): Promise<ApiResponse<void>> => {
  try {
    const recipeIdStr = String(recipeId);
    const ingredientIdStr = String(ingredientId);

    await http.delete(`/recipes/${recipeIdStr}/ingredients/${ingredientIdStr}`);

    return {
      success: true,
      data: undefined,
    };
  } catch (error) {
    return {
      success: false,
      error: errorHandler(error),
    };
  }
};
